import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}

const WIDTHS = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' } as const;

export function Modal({ title, description, onClose, children, footer, width = 'md' }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    // Move focus into the dialog so keyboard users are not left behind on the
    // page underneath, and stop the background scrolling under the overlay.
    panelRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in"
      onMouseDown={(event) => {
        // mousedown rather than click: a click handler fires when a drag that
        // started inside the dialog (selecting text) ends on the overlay,
        // closing the dialog and losing whatever was typed.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/*
        Header and footer are pinned; only the body scrolls.
        Previously the whole dialog scrolled inside the overlay, so on a long
        form the confirm button sat far below the field that enables it — and
        a disabled button with its explanation off-screen reads as broken.
      */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          'flex max-h-[calc(100vh-2rem)] w-full flex-col animate-slide-up panel rounded-xl',
          WIDTHS[width],
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-edge px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
            {description && (
              <p className="mt-0.5 text-2xs leading-relaxed text-ink-muted">{description}</p>
            )}
          </div>
          <button className="btn btn-ghost px-1.5" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-edge px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
